/**
 * C1 c1-price-run-signal-rows (CLB-022, DEC-1365): the Price Run's margin and lens cells as pure signal. Each cell is the served
 * figure on the band C2 gave it (the grid row's `bands`); the portal never classifies a value (INV-12). Colours are the vendored
 * band_colors.py variables (`src/styles/brand/kpi_bands.css`), never a local map.
 */
import type { Band } from "./kpi";
import type { AdsMode } from "./contribution";

export type SignalKey = "contribution" | "fulfilment" | "gross" | "customer" | "competition";
export const SIGNAL_KEYS: SignalKey[] = ["contribution", "fulfilment", "gross", "customer", "competition"];
export type BandState = "banded" | "no_target" | "no_data";
/** One registered range as c2-row-bands v1.0 serves it, worst to best: the set the figure was classified on. */
export type BandRange = { band: Band; low: string | null; high: string | null };
/** One cell's band as C2 serves it on a row (c2-row-bands v1.0); `kpi_name`, `description` and `ranges` feed the header tip (D2). */
export type CellBand = {
  band: Band | null; state: BandState; kpi_id: string | null;
  kpi_name?: string | null; description?: string | null; ranges?: BandRange[] | null;
};
/** The row's `bands` (C2, optional), on a grid row and on an order-type view row with figures. `channel` (k580) has no Price Run
 * column in the approved v28 layout and is not read. `contribution_ex_ads`, when served, is the band without ad spend (PP-01.3). */
export type RowBands = Partial<Record<SignalKey | "channel" | "contribution_ex_ads", CellBand | null>>;

/** What a cell paints: `neutral` when C2 named no band for it (an older API, or an absent entry, including a view row without its
 * own `bands`: E2). */
export type Paint = { state: BandState | "neutral"; band: Band | null; background: string; ink: "white" | "dark" };

export const SIGNAL_COPY = {
  noData: "no data",
  noTarget: "No target",
  noDataLegend: "No data",
  legendLabel: "Band",
  info: (column: string) => `About ${column}`,
} as const;

const SLUG: Record<Band, string> = { Critical: "critical", "@ Risk": "at-risk", Good: "good", Excellent: "excellent", Outstanding: "outstanding" };
/** White text on the two dark bands, dark on the rest (UX v0.32 §9.10 BandCell). */
const WHITE_TEXT = new Set<Band>(["Critical", "Outstanding"]);

/** The served band entry behind a column, or null. The caller passes the bands of the figures on screen: the grid row's, or in an
 * order-type view the view row's (E2), never the list-price bands under a view's figures. Contribution follows the Ads toggle:
 * without a served `contribution_ex_ads` the excluded figure paints neutral. */
export function cellBand(bands: RowBands | null | undefined, key: SignalKey, ads: AdsMode): CellBand | null {
  if (!bands) return null;
  const k = key === "contribution" && ads === "excluded" ? "contribution_ex_ads" : key;
  return bands[k] ?? null;
}

/** The background and ink for a cell, from the served state alone. A banded entry without a band name paints neutral. */
export function paintOf(entry: CellBand | null): Paint {
  const neutral: Paint = { state: "neutral", band: null, background: "var(--ks-band-no-data)", ink: "dark" };
  if (!entry) return neutral;
  if (entry.state === "no_target") return { state: "no_target", band: null, background: "var(--ks-band-no-target)", ink: "dark" };
  if (entry.state === "no_data") return { state: "no_data", band: null, background: "var(--ks-band-no-data)", ink: "dark" };
  if (entry.state === "banded" && entry.band && SLUG[entry.band]) {
    return { state: "banded", band: entry.band, background: `var(--ks-band-${SLUG[entry.band]})`, ink: WHITE_TEXT.has(entry.band) ? "white" : "dark" };
  }
  return neutral;
}

/** The legend above the table, in the band order the KPI system lists them, then the two greys. */
export const LEGEND: Array<{ label: string; background: string }> = [
  ...(["Critical", "@ Risk", "Good", "Excellent", "Outstanding"] as Band[]).map((b) => ({ label: b, background: `var(--ks-band-${SLUG[b]})` })),
  { label: SIGNAL_COPY.noTarget, background: "var(--ks-band-no-target)" },
  { label: SIGNAL_COPY.noDataLegend, background: "var(--ks-band-no-data)" },
];

/** The header tip for a column (D2): the first served entry on view that names its KPI. Null when no row names one. */
export function headerKpi(entries: Array<CellBand | null>): { name: string; description: string | null; ranges: BandRange[] } | null {
  const named = entries.find((e) => e?.kpi_name);
  if (!named?.kpi_name) return null;
  return { name: named.kpi_name, description: named.description ?? null, ranges: named.ranges ?? [] };
}
/** One served range as the KPI Targets tab holds it ("Good 25 to 35"); either end may be absent. */
export function rangeText(r: BandRange): string {
  const ends = [r.low, r.high].filter((v): v is string => v != null && v !== "");
  return ends.length ? `${r.band} ${ends.join(" to ")}` : r.band;
}
